// Drawing Relay Manager - manages relay game state, timer, and overlay UI

interface RelayState {
  status: string;
  turnDuration: number;
  currentRound: number;
  maxRounds: number;
  currentTurnIndex: number;
  turnOrder: number[];
  currentDrawerId: number | null;
  turnStartedAt: string | null;
  serverTime: string;
  startedAt: string | null;
  finishedAt: string | null;
}

interface RelayStartOptions {
  turnDuration: number;
  maxRounds: number;
}

type DrawingEnabledCallback = (enabled: boolean) => void;

export class RelayManager {
  private roomCode: string;
  private userId: number;
  private userName: string;
  private isHost: boolean;
  private onDrawingEnabledChange: DrawingEnabledCallback;

  private channel: any = null;
  private state: RelayState | null = null;
  private clockSkew: number = 0;
  private timerInterval: number | null = null;
  private advanceTimeout: number | null = null;
  private overlayEl: HTMLElement | null = null;
  private turnTransitionTimeout: number | null = null;

  // Participant name cache
  private participantNames: Map<number, string> = new Map();

  constructor(
    roomCode: string,
    userId: number,
    userName: string,
    isHost: boolean,
    onDrawingEnabledChange: DrawingEnabledCallback
  ) {
    this.roomCode = roomCode;
    this.userId = userId;
    this.userName = userName;
    this.isHost = isHost;
    this.onDrawingEnabledChange = onDrawingEnabledChange;
    this.overlayEl = document.getElementById('drawing-relay-overlay');

    this.setupHostButtons();
  }

  bindToChannel(channel: any): void {
    if (!channel) return;
    this.channel = channel;

    channel.bind('relay-started', (data: RelayState) => {
      this.onRelayStarted(data);
    });

    channel.bind('relay-turn-changed', (data: RelayState) => {
      this.onTurnChanged(data);
    });

    channel.bind('relay-finished', (data: RelayState) => {
      this.onRelayFinished(data);
    });

    // Handle member removal - host advances if current drawer left
    channel.bind('pusher:member_removed', (member: any) => {
      if (!this.state || this.state.status !== 'playing') return;
      const removedId = parseInt(member.id, 10);
      if (removedId === this.state.currentDrawerId && this.isHost) {
        this.postAdvance();
      }
    });

    // Collect participant names from presence channel members
    channel.bind('pusher:subscription_succeeded', (members: any) => {
      if (members && members.each) {
        members.each((member: any) => {
          if (member.info?.name) {
            this.participantNames.set(parseInt(member.id, 10), member.info.name);
          }
        });
      }
    });

    channel.bind('pusher:member_added', (member: any) => {
      if (member.info?.name) {
        this.participantNames.set(parseInt(member.id, 10), member.info.name);
      }
    });
  }

  private setupHostButtons(): void {
    const startBtn = document.getElementById('relay-start-btn');
    const stopBtn = document.getElementById('relay-stop-btn');

    if (this.isHost && startBtn) {
      startBtn.classList.remove('hidden');
      startBtn.classList.add('flex');

      startBtn.addEventListener('click', () => {
        this.showSettingsDropdown();
      });
    }

    if (this.isHost && stopBtn) {
      stopBtn.addEventListener('click', () => {
        this.stopRelay();
      });
    }
  }

  private showSettingsDropdown(): void {
    // Remove existing dropdown
    const existing = document.getElementById('relay-settings-dropdown');
    if (existing) { existing.remove(); return; }

    const startBtn = document.getElementById('relay-start-btn');
    if (!startBtn) return;

    const dropdown = document.createElement('div');
    dropdown.id = 'relay-settings-dropdown';
    dropdown.className = 'absolute top-12 right-0 bg-gray-800 border border-gray-700 rounded-lg p-4 shadow-xl z-50';
    dropdown.style.minWidth = '220px';
    dropdown.innerHTML = `
      <div class="text-white text-sm font-bold mb-3">릴레이 설정</div>
      <div class="mb-3">
        <label class="text-gray-400 text-xs block mb-1">턴 시간</label>
        <select id="relay-turn-duration" class="w-full bg-gray-700 text-white text-sm rounded px-2 py-1.5 border border-gray-600">
          <option value="15">15초</option>
          <option value="30" selected>30초</option>
          <option value="60">60초</option>
        </select>
      </div>
      <div class="mb-3">
        <label class="text-gray-400 text-xs block mb-1">라운드 수</label>
        <select id="relay-max-rounds" class="w-full bg-gray-700 text-white text-sm rounded px-2 py-1.5 border border-gray-600">
          <option value="1">1 라운드</option>
          <option value="2">2 라운드</option>
          <option value="3" selected>3 라운드</option>
          <option value="4">4 라운드</option>
          <option value="5">5 라운드</option>
        </select>
      </div>
      <button id="relay-settings-start" class="w-full bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold rounded-lg py-2 transition-colors">
        시작하기
      </button>
    `;

    startBtn.parentElement!.style.position = 'relative';
    startBtn.parentElement!.appendChild(dropdown);

    document.getElementById('relay-settings-start')?.addEventListener('click', () => {
      const duration = parseInt((document.getElementById('relay-turn-duration') as HTMLSelectElement).value, 10);
      const rounds = parseInt((document.getElementById('relay-max-rounds') as HTMLSelectElement).value, 10);
      dropdown.remove();
      this.startRelay({ turnDuration: duration, maxRounds: rounds });
    });

    // Close on outside click
    const closeHandler = (e: MouseEvent) => {
      if (!dropdown.contains(e.target as Node) && e.target !== startBtn) {
        dropdown.remove();
        document.removeEventListener('click', closeHandler);
      }
    };
    setTimeout(() => document.addEventListener('click', closeHandler), 0);
  }

  async startRelay(options: RelayStartOptions): Promise<void> {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
    try {
      const resp = await fetch(`/drawing/room/${this.roomCode}/relay/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          turn_duration: options.turnDuration,
          max_rounds: options.maxRounds,
        }),
      });
      const data = await resp.json();
      if (!data.success) {
        alert(data.error || '릴레이 시작 실패');
      }
    } catch (e) {
      console.error('[RelayManager] Start failed:', e);
    }
  }

  async stopRelay(): Promise<void> {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
    try {
      await fetch(`/drawing/room/${this.roomCode}/relay/stop`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
      });
    } catch (e) {
      console.error('[RelayManager] Stop failed:', e);
    }
  }

  private async postAdvance(): Promise<void> {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
    try {
      await fetch(`/drawing/room/${this.roomCode}/relay/advance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
      });
    } catch (e) {
      console.error('[RelayManager] Advance failed:', e);
    }
  }

  private onRelayStarted(data: RelayState): void {
    this.state = data;
    this.calculateClockSkew(data.serverTime);
    this.updateDrawingEnabled();
    this.showHostButtons(true);
    this.startTimer();
    this.renderOverlay();
    this.showTurnTransition();
  }

  private onTurnChanged(data: RelayState): void {
    this.state = data;
    this.calculateClockSkew(data.serverTime);
    this.updateDrawingEnabled();
    this.startTimer();
    this.renderOverlay();
    this.showTurnTransition();
  }

  private onRelayFinished(data: RelayState): void {
    this.state = data;
    this.stopTimer();
    this.onDrawingEnabledChange(true);
    this.showHostButtons(false);
    this.renderFinishedOverlay();

    // Clear overlay after 3 seconds
    setTimeout(() => {
      this.clearOverlay();
      this.setToolbarOpacity(1);
    }, 3000);
  }

  private calculateClockSkew(serverTimeStr: string): void {
    const serverTime = new Date(serverTimeStr).getTime();
    this.clockSkew = serverTime - Date.now();
  }

  private getAdjustedNow(): number {
    return Date.now() + this.clockSkew;
  }

  private updateDrawingEnabled(): void {
    if (!this.state || this.state.status !== 'playing') {
      this.onDrawingEnabledChange(true);
      this.setToolbarOpacity(1);
      return;
    }

    const isMyTurn = this.state.currentDrawerId === this.userId;
    this.onDrawingEnabledChange(isMyTurn);
    this.setToolbarOpacity(isMyTurn ? 1 : 0.5);
  }

  private setToolbarOpacity(opacity: number): void {
    const toolbar = document.getElementById('drawing-toolbar');
    const props = document.getElementById('drawing-properties');
    const colorPicker = document.getElementById('drawing-color-picker');
    [toolbar, props, colorPicker].forEach(el => {
      if (el) el.style.opacity = String(opacity);
    });
  }

  private showHostButtons(playing: boolean): void {
    const startBtn = document.getElementById('relay-start-btn');
    const stopBtn = document.getElementById('relay-stop-btn');

    if (this.isHost) {
      if (startBtn) {
        startBtn.classList.toggle('hidden', playing);
        startBtn.classList.toggle('flex', !playing);
      }
      if (stopBtn) {
        stopBtn.classList.toggle('hidden', !playing);
        stopBtn.classList.toggle('flex', playing);
      }
    }
  }

  private startTimer(): void {
    this.stopTimer();

    this.timerInterval = window.setInterval(() => {
      this.updateTimerUI();
    }, 1000);

    // Schedule advance
    this.scheduleAdvance();
  }

  private stopTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    if (this.advanceTimeout) {
      clearTimeout(this.advanceTimeout);
      this.advanceTimeout = null;
    }
  }

  private getTimeRemaining(): number {
    if (!this.state || !this.state.turnStartedAt) return 0;
    const turnStart = new Date(this.state.turnStartedAt).getTime();
    const elapsed = (this.getAdjustedNow() - turnStart) / 1000;
    return Math.max(0, this.state.turnDuration - elapsed);
  }

  private scheduleAdvance(): void {
    if (this.advanceTimeout) clearTimeout(this.advanceTimeout);

    const remaining = this.getTimeRemaining();
    const isCurrentDrawer = this.state?.currentDrawerId === this.userId;

    // Current drawer advances at 0s, others at +2s grace period
    const delay = isCurrentDrawer ? remaining * 1000 : (remaining + 2) * 1000;

    this.advanceTimeout = window.setTimeout(() => {
      if (this.state?.status === 'playing') {
        this.postAdvance();
      }
    }, Math.max(0, delay));
  }

  private updateTimerUI(): void {
    const timerEl = document.getElementById('relay-timer-value');
    if (!timerEl) return;

    const remaining = Math.ceil(this.getTimeRemaining());
    timerEl.textContent = `${remaining}초`;

    // Color based on time
    if (remaining <= 5) {
      timerEl.className = 'text-red-400 font-bold animate-pulse';
    } else if (remaining <= 10) {
      timerEl.className = 'text-yellow-400 font-bold';
    } else {
      timerEl.className = 'text-green-400 font-bold';
    }
  }

  private getParticipantName(userId: number): string {
    if (userId === this.userId) return this.userName;
    return this.participantNames.get(userId) || `User ${userId}`;
  }

  private renderOverlay(): void {
    if (!this.overlayEl || !this.state) return;

    const isMyTurn = this.state.currentDrawerId === this.userId;
    const currentDrawerName = this.state.currentDrawerId
      ? this.getParticipantName(this.state.currentDrawerId)
      : '?';
    const remaining = Math.ceil(this.getTimeRemaining());

    // Build turn order display
    const turnOrderHTML = this.state.turnOrder.map((uid, idx) => {
      const name = uid === this.userId ? 'You' : this.getParticipantName(uid);
      const isCurrent = idx === this.state!.currentTurnIndex;
      const cls = isCurrent ? 'text-purple-300 font-bold' : 'text-gray-500';
      return `<span class="${cls}">${name}</span>`;
    }).join(' <span class="text-gray-600">&rarr;</span> ');

    // Timer color class
    let timerClass = 'text-green-400 font-bold';
    if (remaining <= 5) timerClass = 'text-red-400 font-bold animate-pulse';
    else if (remaining <= 10) timerClass = 'text-yellow-400 font-bold';

    this.overlayEl.innerHTML = `
      <div style="pointer-events: none;" class="fixed top-14 left-1/2 -translate-x-1/2 z-[10000]">
        <div class="bg-gray-900/90 backdrop-blur border border-gray-700 rounded-xl px-5 py-2.5 shadow-2xl flex items-center space-x-4 text-sm">
          <span class="text-gray-400">라운드 <span class="text-white font-bold">${this.state.currentRound}/${this.state.maxRounds}</span></span>
          <span class="text-gray-600">|</span>
          <span class="text-gray-400">🎨 <span class="text-white font-medium">${currentDrawerName}</span> 차례</span>
          <span class="text-gray-600">|</span>
          <span class="text-gray-400">⏱ <span id="relay-timer-value" class="${timerClass}">${remaining}초</span></span>
        </div>
        <div class="text-center mt-1.5 text-xs">${turnOrderHTML}</div>
      </div>
      ${!isMyTurn ? `
        <div style="pointer-events: none;" class="fixed inset-0 z-[9999] border-4 border-purple-500/30 rounded-none">
          <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-purple-400/40">
            <svg class="w-16 h-16" fill="currentColor" viewBox="0 0 24 24"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>
          </div>
        </div>
      ` : ''}
    `;
  }

  private showTurnTransition(): void {
    if (this.turnTransitionTimeout) clearTimeout(this.turnTransitionTimeout);

    if (!this.state || !this.overlayEl) return;

    const isMyTurn = this.state.currentDrawerId === this.userId;
    const drawerName = this.state.currentDrawerId
      ? this.getParticipantName(this.state.currentDrawerId)
      : '?';
    const message = isMyTurn ? '당신 차례!' : `${drawerName} 차례`;
    const color = isMyTurn ? 'text-purple-300' : 'text-gray-300';

    const transitionEl = document.createElement('div');
    transitionEl.style.pointerEvents = 'none';
    transitionEl.className = `fixed inset-0 z-[10001] flex items-center justify-center`;
    transitionEl.innerHTML = `
      <div class="text-4xl font-black ${color} animate-bounce drop-shadow-2xl">
        ${message}
      </div>
    `;

    this.overlayEl.appendChild(transitionEl);

    this.turnTransitionTimeout = window.setTimeout(() => {
      transitionEl.remove();
    }, 1500);
  }

  private renderFinishedOverlay(): void {
    if (!this.overlayEl) return;

    this.overlayEl.innerHTML = `
      <div style="pointer-events: none;" class="fixed top-14 left-1/2 -translate-x-1/2 z-[10000]">
        <div class="bg-gray-900/90 backdrop-blur border border-green-700 rounded-xl px-5 py-2.5 shadow-2xl text-sm text-center">
          <span class="text-green-400 font-bold">릴레이 완료!</span>
        </div>
      </div>
    `;
  }

  private clearOverlay(): void {
    if (this.overlayEl) {
      this.overlayEl.innerHTML = '';
    }
  }

  destroy(): void {
    this.stopTimer();
    if (this.turnTransitionTimeout) clearTimeout(this.turnTransitionTimeout);
    this.clearOverlay();

    if (this.channel) {
      this.channel.unbind('relay-started');
      this.channel.unbind('relay-turn-changed');
      this.channel.unbind('relay-finished');
    }
  }
}
